import {
  ActionIcon,
  Avatar,
  Box,
  Checkbox,
  ColorInput,
  FileButton,
  Group,
  Input,
  NumberInput,
  Select,
  Slider,
  Stack,
  Text,
} from '@mantine/core';
import { useHover } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { I_IconSetting, useWidgetFormContext } from '@/components/Widget/Embed/form-context';
import { useWidgetForm } from '@/hooks/useWidgetForm';

import classes from './chatbotIcon.module.css';
import {
  SvgAddPicture,
  SvgChatbotIcon_1,
  SvgChatbotIcon_2,
  SvgChatbotIcon_3,
  SvgChatbotIcon_4,
  SvgChatbotIcon_5,
  SvgChatbotIcon_6,
} from './icon';
import { useCanAccess } from '@mygpt/app';

import { SettingCard } from './SettingCard';

export const CHATBOT_ICON_ARRAY = [
  SvgChatbotIcon_1,
  SvgChatbotIcon_2,
  SvgChatbotIcon_3,
  SvgChatbotIcon_4,
  SvgChatbotIcon_5,
  SvgChatbotIcon_6,
];

/** 气泡图标：仅小部件生效（嵌入小窗的浮动气泡） */
export default () => {
  const { t } = useTranslation();
  const { hovered, ref } = useHover();
  const { form } = useWidgetFormContext();

  const { onOverrideChangeForForm } = useWidgetForm();
  const canAccessWidgetCustomization = useCanAccess('enable_widget_customization');

  const [customFileSrc, setCustomFileSrc] = useState<string>('');
  const resetRef = useRef<() => void>(null);

  const changeFormChatBotIcon = (param: I_IconSetting) => {
    form.setFieldValue('chatbotIcon.icon', { ...param });

    // 模仿触发了 blur 时的保存效果，触发自动保存逻辑
    onOverrideChangeForForm('')({ type: 'blur', isTrusted: true });
  };

  const systemFile = (index: number) => {
    setCustomFileSrc('');
    resetRef.current?.();

    changeFormChatBotIcon({
      type: 'system',
      index: index,
      url: '',
    });
  };

  const customFile = (file: File | null) => {
    if (!file) return;

    if (file.size >= 2 * 1024 * 1024) {
      showNotification({
        message: t('embeddings.upload.max_file_size', { size: '2MB' }),
        color: 'red',
      });
      resetRef.current?.();
      return;
    }
    const reads = new FileReader();
    reads.readAsDataURL(file);
    reads.onload = function () {
      const src = this.result as string;
      setCustomFileSrc(src);

      changeFormChatBotIcon({
        type: 'custom',
        url: src,
        file: file,
      });
    };
  };

  const getCustomPreview = () => {
    if (customFileSrc) {
      return <Avatar radius="xl" src={customFileSrc} />;
    }

    return <SvgAddPicture />;
  };

  useEffect(() => {
    setCustomFileSrc(form.values.chatbotIcon.icon.url ?? '');
  }, [form.values]);

  return (
    <SettingCard title={t('widget.setting.groups.card_bubble')}>
      <Box className={classes.chatbotWrap}>
        <Group justify="space-between">
          <Group>
            {CHATBOT_ICON_ARRAY.map((ChatbotIconItem, index) => (
              <ActionIcon
                key={index}
                disabled={!canAccessWidgetCustomization}
                variant={index === form.values.chatbotIcon.icon.index ? 'filled' : 'light'}
                size="xl"
                radius="xl"
                onClick={() => {
                  systemFile(index);
                }}
              >
                <ChatbotIconItem />
              </ActionIcon>
            ))}
          </Group>

          <Group>
            <section className={classes.boxVerticalLine} />
            <Box ref={ref}>
              <FileButton
                resetRef={resetRef}
                onChange={customFile}
                accept="image/png,image/jpeg"
                disabled={!canAccessWidgetCustomization}
              >
                {(props) => (
                  <ActionIcon
                    className={classes.hoverBtn}
                    variant="outline"
                    size="xl"
                    radius="xl"
                    {...props}
                    disabled={!canAccessWidgetCustomization}
                  >
                    {canAccessWidgetCustomization && hovered ? <IconPlus /> : getCustomPreview()}
                  </ActionIcon>
                )}
              </FileButton>
            </Box>
          </Group>
        </Group>
      </Box>

      <Stack mt={30}>
        <Group justify="space-between" wrap="nowrap">
          <Checkbox
            disabled={!canAccessWidgetCustomization}
            label={t('widget.setting.chatbot_icon.position_label')}
            checked={form.values.chatbotIcon.position.checked}
            {...form.getInputProps('chatbotIcon.position.checked')}
            onChange={onOverrideChangeForForm('chatbotIcon.position.checked')}
          />
          <section className="w-[200px]">
            <Select
              disabled={!form.values.chatbotIcon.position.checked}
              size="sm"
              data={[
                { value: 'bottom-right', label: t('widget.setting.chatbot_icon.bottom_right') },
                { value: 'bottom-left', label: t('widget.setting.chatbot_icon.bottom_left') },
                { value: 'top-right', label: t('widget.setting.chatbot_icon.top_right') },
                { value: 'top-left', label: t('widget.setting.chatbot_icon.top_left') },
              ]}
              value={
                // 兼容旧值：left → bottom-left, right → bottom-right
                form.values.chatbotIcon.position.value === 'left'
                  ? 'bottom-left'
                  : form.values.chatbotIcon.position.value === 'right'
                  ? 'bottom-right'
                  : form.values.chatbotIcon.position.value
              }
              onChange={onOverrideChangeForForm('chatbotIcon.position.value')}
            />
          </section>
        </Group>

        <Group justify="space-between" wrap="nowrap">
          <Checkbox
            disabled={!canAccessWidgetCustomization}
            label={t('widget.setting.chatbot_icon.custom_position_label')}
            checked={form.values.chatbotIcon.customPosition.checked}
            {...form.getInputProps('chatbotIcon.customPosition.checked')}
            onChange={onOverrideChangeForForm('chatbotIcon.customPosition.checked')}
          />
          <Group gap={8} className="w-[200px]">
            <Group gap={4} wrap="nowrap">
              <Text size="sm" c="gray.6">
                X:
              </Text>
              <Input
                placeholder="16"
                size="sm"
                type="number"
                min={0}
                rightSection={
                  <Text size="xs" c="gray.6">
                    px
                  </Text>
                }
                rightSectionWidth={24}
                value={form.values.chatbotIcon.customPosition.x || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // 允许空字符串或有效数字
                  if (value === '' || !isNaN(Number(value))) {
                    form.setFieldValue('chatbotIcon.customPosition.x', value === '' ? 0 : parseInt(value));
                  }
                }}
                onBlur={(e) => {
                  // blur时确保有默认值
                  if (e.target.value === '') {
                    form.setFieldValue('chatbotIcon.customPosition.x', 0);
                  }
                  onOverrideChangeForForm('chatbotIcon.customPosition.x')(e);
                }}
                disabled={!form.values.chatbotIcon.customPosition.checked || !canAccessWidgetCustomization}
              />
            </Group>
            <Group gap={4} wrap="nowrap">
              <Text size="sm" c="gray.6">
                Y:
              </Text>
              <Input
                placeholder="16"
                size="sm"
                type="number"
                min={0}
                rightSection={
                  <Text size="xs" c="gray.6">
                    px
                  </Text>
                }
                rightSectionWidth={24}
                value={form.values.chatbotIcon.customPosition.y || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // 允许空字符串或有效数字
                  if (value === '' || !isNaN(Number(value))) {
                    form.setFieldValue('chatbotIcon.customPosition.y', value === '' ? 0 : parseInt(value));
                  }
                }}
                onBlur={(e) => {
                  // blur时确保有默认值
                  if (e.target.value === '') {
                    form.setFieldValue('chatbotIcon.customPosition.y', 0);
                  }
                  onOverrideChangeForForm('chatbotIcon.customPosition.y')(e);
                }}
                disabled={!form.values.chatbotIcon.customPosition.checked || !canAccessWidgetCustomization}
              />
            </Group>
          </Group>
        </Group>

        <Group justify="space-between" wrap="nowrap">
          <Checkbox
            disabled={!canAccessWidgetCustomization}
            label={t('widget.setting.chatbot_icon.color_label')}
            checked={form.values.chatbotIcon.color.checked}
            {...form.getInputProps('chatbotIcon.color.checked')}
            onChange={onOverrideChangeForForm('chatbotIcon.color.checked')}
          />
          <section className="w-[200px]">
            <ColorInput
              disabled={!form.values.chatbotIcon.color.checked}
              autoComplete="off"
              {...form.getInputProps('chatbotIcon.color.value')}
              onChange={onOverrideChangeForForm('chatbotIcon.color.value')}
            />
          </section>
        </Group>

        <Group justify="space-between" wrap="nowrap">
          <Checkbox
            disabled={!canAccessWidgetCustomization}
            label={t('widget.setting.chatbot_icon.icon_button_text_label')}
            checked={form.values.chatbotIcon.iconButtonText.checked}
            {...form.getInputProps('chatbotIcon.iconButtonText.checked')}
            onChange={onOverrideChangeForForm('chatbotIcon.iconButtonText.checked')}
          />
          <section className="w-[200px]">
            <Input
              disabled={!form.values.chatbotIcon.iconButtonText.checked}
              autoComplete="off"
              maxLength={24}
              {...form.getInputProps('chatbotIcon.iconButtonText.value')}
              onBlur={onOverrideChangeForForm('chatbotIcon.iconButtonText.value')}
            />
          </section>
        </Group>

        <Group justify="space-between" wrap="nowrap">
          <Checkbox
            disabled={!canAccessWidgetCustomization}
            label={t('widget.setting.chatbot_icon.icon_button_size_label')}
            checked={form.values.chatbotIcon.iconButtonSize.checked}
            {...form.getInputProps('chatbotIcon.iconButtonSize.checked')}
            onChange={onOverrideChangeForForm('chatbotIcon.iconButtonSize.checked')}
          />
          <section className="w-[200px]">
            <Slider
              label={null}
              step={50}
              marks={[
                { value: 0, label: t('widget.setting.chatbot_icon.small') },
                { value: 50, label: t('widget.setting.chatbot_icon.medium') },
                { value: 100, label: t('widget.setting.chatbot_icon.large') },
              ]}
              disabled={!canAccessWidgetCustomization || !form.values.chatbotIcon.iconButtonSize.checked}
              {...form.getInputProps('chatbotIcon.iconButtonSize.value')}
              onChange={onOverrideChangeForForm('chatbotIcon.iconButtonSize.value')}
            />
          </section>
        </Group>

        <Group justify="space-between" wrap="nowrap" mt={24}>
          <Stack gap={0}>
            <Text size={'sm'}>{t('widget.setting.chatbot_icon.stay_page_label')}</Text>
            <Text size="xs" c="gray.6">
              {t('widget.setting.chatbot_icon.stay_page_desc')}
            </Text>
          </Stack>
          <section className="w-[200px]">
            <NumberInput
              min={0}
              disabled={!form.values.chatbot.welcomeMessage.checked}
              autoComplete="off"
              {...form.getInputProps('chatbotIcon.stayPage.value')}
              onBlur={onOverrideChangeForForm('chatbotIcon.stayPage.value')}
            />
          </section>
        </Group>
      </Stack>
    </SettingCard>
  );
};
